import { Typography } from "@mui/material";
import { OrderDto, RelationType } from "Api/Api";
import { BlButton } from "Components/Shared/Buttons/BlButton";
import { BlConfirmDialog } from "Components/Shared/Dialogs/BlConfirmDialog";
import { FlagIcon } from "Components/Shared/Icons";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { useAppDispatch } from "Hooks/State/useAppDispatch";
import { deleteOrderAsync } from "State/Orders/Detail/DeleteOrderSlice";
import { Resources, useResource } from "Translations/Resources";
import { AppRouting, getPath } from "Utils/UrlUtils";
import { PropsWithChildren, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import styled, { useTheme } from "styled-components";

const CopyOrderBoxWrapper = styled.div`
  border-radius: 12px;
  border: 1px solid;
  border-color: ${props => props.theme.palette.error.light};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(1)};
`;

const ContentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
`;

type CopyOrderNoticeProps = {
  title: string;
  description: string;
  primaryLabel: string;
  onPrimary: () => void;
  showDelete?: boolean;
  onDelete?: () => void;
  disabled?: boolean;
};

const CopyOrderNotice = ({
  title,
  description,
  primaryLabel,
  onPrimary,
  showDelete,
  onDelete,
  disabled,
}: PropsWithChildren<CopyOrderNoticeProps>) => {
  const theme = useTheme();
  return (
    <CopyOrderBoxWrapper>
      <StyledFlex $gap={1}>
        <FlagIcon color={theme.palette.error.light} />
        <Typography
          color={t => t.palette.error.light}
          fontWeight={700}
          fontSize={18}
        >
          {title}
        </Typography>
      </StyledFlex>

      <ContentRow>
        <Typography>{description}</Typography>
        <div style={{ display: "flex", gap: theme.spacing(1) }}>
          <BlButton onClick={onPrimary}>{primaryLabel}</BlButton>
          {showDelete && (
            <BlButton color="error" onClick={onDelete} disabled={disabled}>
              Smazat
            </BlButton>
          )}
        </div>
      </ContentRow>
    </CopyOrderBoxWrapper>
  );
};

type Props = { order: OrderDto };

export const CopyOrderHeaderSection: React.FunctionComponent<Props> = ({
  order,
}) => {
  const { t } = useResource();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [orderPublicIDToDelete, setOrderPublicIDToDelete] = useState<
    string | null
  >(null);

  const isReadOnly = order.userRelationType === RelationType.Visibility;
  const disabled = isReadOnly;

  const goToOrder = (publicID: string) => {
    navigate(getPath(AppRouting.OrderDetail, publicID));
  };

  const {
    activeCopyOrder,
    draftCopyOrder,
    rejectedCopyOrder,
    unactiveCopyOrderWhichWasActived, //kopie obj. která není nyní už aktivní, ale v minulosti byla, tedy na nic odkazujeme
  } = useMemo(() => {
    const unactiveCopyOrderWhichWasActived = order.copiedOrders.find(
      x => x.activeDateFrom && !x.isActive,
    );
    const activeCopyOrder = order.copiedOrders.find(x => x.isActive);
    const draftCopryOrder = order.copiedOrders.find(
      x => !x.isRejected && !x.isActive && !x.activeDateFrom,
    );
    const rejectedCopyOrder =
      !activeCopyOrder && !draftCopryOrder
        ? order.copiedOrders.find(x => x.isRejected)
        : undefined;
    return {
      activeCopyOrder: activeCopyOrder,
      draftCopyOrder: draftCopryOrder,
      rejectedCopyOrder: rejectedCopyOrder,
      unactiveCopyOrderWhichWasActived: unactiveCopyOrderWhichWasActived,
    };
  }, [order.copiedOrders]);

  const handleDeleteConfirm = (isAccepted: boolean) => {
    if (disabled) return;
    if (orderPublicIDToDelete && isAccepted) {
      dispatch(
        deleteOrderAsync.request({
          publicID: orderPublicIDToDelete,
          deleteCopyOrder: true,
          navigate,
        }),
      );
    }
    setOrderPublicIDToDelete(null);
  };

  return (
    <>
      {!!order.copiedFromOrderPublicID && <></>}

      {unactiveCopyOrderWhichWasActived && (
        <CopyOrderNotice
          title={t(Resources.EucsDisableService.Title)}
          description="Jedná se o historickou verzi objednávky, která již není aktivní."
          primaryLabel="Zobrazit objednávku"
          onPrimary={() =>
            goToOrder(unactiveCopyOrderWhichWasActived.orderPublicID)
          }
        />
      )}

      {activeCopyOrder && (
        <CopyOrderNotice
          title={t(Resources.EucsDisableService.Title)}
          description="Jedná se o historickou verzi objednávky, která již není aktivní."
          primaryLabel="Zobrazit aktivní"
          onPrimary={() => goToOrder(activeCopyOrder.orderPublicID)}
        />
      )}

      {draftCopyOrder && (
        <CopyOrderNotice
          title={t(Resources.EucsDisableService.Title)}
          description="Máte rozpracovanou editaci této objednávky, jakmile ji odešlete klientovi a ten ji schválí, stane se aktivní."
          primaryLabel="Zobrazit"
          onPrimary={() => goToOrder(draftCopyOrder.orderPublicID)}
          showDelete
          onDelete={() =>
            setOrderPublicIDToDelete(draftCopyOrder.orderPublicID)
          }
          disabled={disabled}
        />
      )}

      {rejectedCopyOrder && (
        <CopyOrderNotice
          title={t(Resources.EucsDisableService.Title)}
          description="Máte rozpracovanou editaci této objednávky, která byla klientem odmítnuta."
          primaryLabel="Zobrazit"
          onPrimary={() => goToOrder(rejectedCopyOrder.orderPublicID)}
          showDelete
          onDelete={() =>
            setOrderPublicIDToDelete(rejectedCopyOrder.orderPublicID)
          }
          disabled={disabled}
        />
      )}

      {!disabled && (draftCopyOrder || rejectedCopyOrder) && (
        <BlConfirmDialog
          isOpen={orderPublicIDToDelete != null}
          handleResult={() => handleDeleteConfirm(true)}
          modalTitle={t(Resources.Orders.DeleteConfirmDialog.Title)}
          modalContent={t(Resources.Orders.DeleteConfirmDialog.Text)}
        />
      )}
    </>
  );
};
